import React, { useEffect, useState } from 'react';
import { Button, Table, Header, Input, Message } from 'semantic-ui-react';
import { toast } from '../../../common/Toast';
import LocalStorage from '../../../../lib/LocalStorage';
import config from '../../../../common/repository/config';
import UpdateStackHolder from './UpdateStackHolder';

interface StakeHolderRole {
  _id: string;
  name: string;
}
interface StackHolderListProps {
  reloadFlag: boolean;
}

const StackHolderList: React.FC<StackHolderListProps> = ({ reloadFlag }) => {
  const [stackHolderRoles, setStackHolderRoles] = useState<StakeHolderRole[]>([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [noDataMessage, setNoDataMessage] = useState('');
  // const [reloadFlag, setReloadFlag] = useState(false);

  const [editingRole, setEditingRole] = useState<StakeHolderRole | null>(null);

  const handleEditClick = (role: StakeHolderRole) => {
    setEditingRole(role);
  };

  const handleCloseEdit = () => {
    setEditingRole(null);
  };

  const handleUpdateSuccess = () => {
    fetchStakeHolderRoles();
  };

  const fetchStakeHolderRoles = async () => {
    setLoading(true);
    setNoDataMessage('');
    try {
      const token = LocalStorage.get(LocalStorage.KEY_AUTH_TOKEN);
      if (!token) throw new Error('Authentication token is missing.');

      const response = await fetch(`${config.apiBaseURL}/stakeHolderRole/listRole`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'x-access-token': token,
        },
      });
      const result = await response.json();

      if (!response.ok || !result?.data?.items) {
        const errorMessage = result?.message || 'Failed to fetch stakeholder roles.';
        setStackHolderRoles([]);
        setNoDataMessage(errorMessage);
        return;
      }

      const items: StakeHolderRole[] = result.data.items;
      if (items.length === 0) {
        setNoDataMessage('No stakeholder roles found.');
      } else {
        setStackHolderRoles(items);
      }
    } catch (error) {
      console.error('Fetch error:', error);
      toast.error('Failed to load stakeholder roles');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (roleId: string) => {
    setLoading(true);
    try {
      const token = LocalStorage.get(LocalStorage.KEY_AUTH_TOKEN);
      if (!token) throw new Error('Authentication token is missing.');

      const response = await fetch(`${config.apiBaseURL}/stakeHolderRole/deleteRole`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'x-access-token': token,
        },
        body: JSON.stringify({ roleId }),
      });

      const result = await response.json();
      if (!response.ok) {
        toast.error(result?.message || 'Failed to delete role.');
        return;
      }

      toast.success(result?.message || 'Role deleted successfully');
      fetchStakeHolderRoles();
    } catch (error) {
      console.error('Delete error:', error);
      toast.error('Error deleting role');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchStakeHolderRoles();
  }, [reloadFlag]);

  if (editingRole) {
    return (
      <UpdateStackHolder
        selectedRole={editingRole}
        onClose={handleCloseEdit}
        onUpdateSuccess={handleUpdateSuccess}
      />
    );
  }

  return (
    <div style={{ padding: '2rem' }}>
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: '1.5rem',
          flexWrap: 'wrap',
          gap: '1rem',
        }}
      >
        <Header as="h2">
          <Header.Content>StakeHolder Roles</Header.Content>
        </Header>

        <div style={{ marginBottom: '1rem', display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
          <Input
            placeholder="Search by name..."
            icon="search"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            style={{ minWidth: '220px' }}
          />
          <Button color="green" loading={loading} onClick={fetchStakeHolderRoles}>
            Search
          </Button>
          <Button onClick={() => setSearchTerm('')}>Clear</Button>
        </div>
      </div>

      {noDataMessage ? (
        <Message warning icon>
          <Message.Content>
            <Message.Header style={{ textAlign: 'center' }}>{noDataMessage}</Message.Header>
          </Message.Content>
        </Message>
      ) : (
        <Table celled striped selectable>
          <Table.Header>
            <Table.Row>
              <Table.HeaderCell>Role Name</Table.HeaderCell>
              <Table.HeaderCell textAlign="center">Actions</Table.HeaderCell>
            </Table.Row>
          </Table.Header>
          <Table.Body>
            {stackHolderRoles
              .filter((role) => role.name.toLowerCase().includes(searchTerm.toLowerCase()))
              .map((role) => (
                <Table.Row key={role._id}>
                  <Table.Cell>{role.name}</Table.Cell>

                  <Table.Cell textAlign="center">
                    <Button
                      size="small"
                      color="blue"
                      onClick={() => handleEditClick(role)}
                      style={{ marginRight: '0.5rem' }}
                    >
                      Edit
                    </Button>
                    <Button size="small" color="red" onClick={() => handleDelete(role._id)}>
                      Delete
                    </Button>
                  </Table.Cell>
                </Table.Row>
              ))}
          </Table.Body>
        </Table>
      )}
    </div>
  );
};

export default StackHolderList;
